import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import logoDark from "assets/muggleTestV0/images/muggleai-logo-dark.png";
import logoWhite from "assets/muggleTestV0/images/muggleai-logo-white.png";
import { useResolvedTheme } from "components/products/muggleTestV0/components/theme-provider";
import { ThemeToggle } from "components/products/muggleTestV0/components/theme-toggle";
import { Button } from "components/products/muggleTestV0/components/ui/button";
import { NAV_ITEMS } from "content/landing";
import { useLandingAuthActions } from "hooks/landing/useLandingAuthActions";
import { scrollToSection } from "utils/landing/scrollUtils";

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { login, signup } = useLandingAuthActions();
  const resolvedTheme = useResolvedTheme();
  const logoSrc = resolvedTheme === "dark" ? logoWhite : logoDark;

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const sectionId = href.replace("#", "");
    scrollToSection(sectionId);
  };

  return (
    <nav
      aria-label="Main navigation"
      className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <a
        href="#overview"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent"
      >
        Skip to content
      </a>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/muggleTestV0" className="flex items-center gap-2">
            <img src={logoSrc} alt="MuggleAI" width={150} height={40} />
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-foreground/80 transition-all hover:bg-muted hover:text-accent"
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="hidden items-center gap-3 md:flex">
            <ThemeToggle />
            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/muggleTestV0/docs">Learning Docs</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => login("nav_login")}>
              Log in
            </Button>
            <Button
              size="sm"
              className="min-h-[44px] bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => signup("nav_signup")}
            >
              Start free
            </Button>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <ThemeToggle />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="border-t border-border md:hidden">
          <div className="space-y-1 px-4 pb-3 pt-2">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="block rounded-md px-3 py-3 text-base font-medium text-foreground/80 hover:bg-muted hover:text-accent"
                onClick={(e) => {
                  handleNavClick(e, item.href);
                  setIsMenuOpen(false);
                }}
              >
                {item.label}
              </a>
            ))}
            <div className="mt-4 flex flex-col gap-2 px-3">
              <Button asChild variant="ghost" size="sm" className="w-full">
                <Link to="/muggleTestV0/docs" onClick={() => setIsMenuOpen(false)}>
                  Learning Docs
                </Link>
              </Button>
              <Button variant="ghost" size="sm" className="w-full" onClick={() => login("nav_login")}>
                Log in
              </Button>
              <Button size="sm" className="min-h-[44px] w-full" onClick={() => signup("nav_signup")}>
                Start free
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
